import type { Node } from '@xyflow/react';
import type { AttackPathEventNodeData } from './attack-path-event-node.types';

export type DestinationPullLevel = 'idle' | 'near' | 'locked';

export const DESTINATION_PULL_NEAR_COLUMNS = 2;

export const isSeedRole = (role: AttackPathEventNodeData['role']): boolean =>
  role === 'OWNED_SEED' ||
  role === 'CROWN_JEWEL_SEED' ||
  role === 'CROWN_JEWEL_CLUSTER';

export const isClimaxNodeData = (data: AttackPathEventNodeData): boolean =>
  data.kind === 'IMPACT_ACHIEVED' ||
  data.isCrownJewel ||
  data.role === 'CROWN_JEWEL_SEED' ||
  data.role === 'CROWN_JEWEL_CLUSTER';

export const resolveRevealActiveCampaignIds = ({
  nodes,
  revealUpToColumn,
}: {
  nodes: ReadonlyArray<Node<AttackPathEventNodeData>>;
  revealUpToColumn: number | undefined;
}): ReadonlySet<string> | null => {
  if (revealUpToColumn === undefined) {
    return null;
  }

  return new Set(
    nodes.flatMap((node) => {
      if (
        node.data.role === 'CAMPAIGN_FRAME' ||
        isSeedRole(node.data.role) ||
        node.data.column !== revealUpToColumn ||
        node.data.campaignId == null
      ) {
        return [];
      }

      return [node.data.campaignId];
    }),
  );
};

export const resolveMaxContentColumn = (
  nodes: ReadonlyArray<Node<AttackPathEventNodeData>>,
): number =>
  nodes.reduce((max, node) => {
    if (node.data.role === 'CAMPAIGN_FRAME' || isSeedRole(node.data.role)) {
      return max;
    }

    return Math.max(max, node.data.column);
  }, 0);

export const resolveDestinationPull = ({
  revealUpToColumn,
  maxContentColumn,
  hasVisibleLink,
}: {
  revealUpToColumn: number | undefined;
  maxContentColumn: number;
  hasVisibleLink: boolean;
}): DestinationPullLevel => {
  if (hasVisibleLink) {
    return 'locked';
  }

  if (revealUpToColumn === undefined) {
    return 'idle';
  }

  if (revealUpToColumn >= maxContentColumn - DESTINATION_PULL_NEAR_COLUMNS) {
    return 'near';
  }

  return 'idle';
};

export const resolveSpotlightDim = ({
  isRevealing,
  hidden,
  column,
  revealUpToColumn,
  role,
  pathInkActive,
  climaxActive,
  destinationPull,
  spotlightActive,
}: {
  isRevealing: boolean;
  hidden: boolean;
  column: number;
  revealUpToColumn: number | undefined;
  role: AttackPathEventNodeData['role'];
  pathInkActive: boolean;
  climaxActive: boolean;
  destinationPull: DestinationPullLevel;
  spotlightActive: boolean;
}): boolean => {
  if (
    !isRevealing ||
    hidden ||
    spotlightActive ||
    pathInkActive ||
    climaxActive ||
    role === 'CAMPAIGN_FRAME'
  ) {
    return false;
  }

  if (
    (role === 'CROWN_JEWEL_SEED' || role === 'CROWN_JEWEL_CLUSTER') &&
    destinationPull !== 'idle'
  ) {
    return false;
  }

  if (role === 'OWNED_SEED') {
    return true;
  }

  if (revealUpToColumn === undefined) {
    return false;
  }

  return column < revealUpToColumn;
};
